// The validation report (spec/dashboard.md section 12.1, R-BR-05, Q-C-2): the issues of `POST …:validate` — or a failed
// release build's `error.details` — grouped by code, then by entity, de-duplicated with counts, errors before warnings,
// every entity a deep link to its editor on the branch.
import { useMemo } from 'react';
import { Link } from 'react-router';
import type { ValidationIssue } from '../api/types';
import { useOptionalContent } from '../content/ContentContext';
import { useGame } from '../shell/GameContext';
import { routes } from '../shell/routes';
import { Badge } from '../ui/Chip';
import './history.css';

export interface IssueLine {
  code: string;
  severity: string;
  entity: string;
  entityId: string | null;
  field: string | null;
  message: string;
  count: number;
}

export interface IssueGroup {
  code: string;
  severity: string;
  lines: IssueLine[];
  count: number;
}

/** De-duplicates by (code, entity, entityId, field, message) with a count; errors first, then by code. */
export function groupIssues(issues: Pick<ValidationIssue, 'code' | 'severity' | 'entity' | 'entityId' | 'field' | 'message'>[]): IssueGroup[] {
  const lines = new Map<string, IssueLine>();
  for (const i of issues) {
    const key = [i.code, i.severity, i.entity, i.entityId ?? '', i.field ?? '', i.message].join('\u0000');
    const line = lines.get(key);
    if (line) line.count++;
    else lines.set(key, { code: i.code, severity: i.severity, entity: i.entity, entityId: i.entityId ?? null, field: i.field ?? null, message: i.message, count: 1 });
  }
  const groups = new Map<string, IssueGroup>();
  for (const l of lines.values()) {
    const key = `${l.severity}:${l.code}`;
    const g = groups.get(key) ?? { code: l.code, severity: l.severity, lines: [], count: 0 };
    g.lines.push(l);
    g.count += l.count;
    groups.set(key, g);
  }
  const rank = (s: string) => (s === 'error' ? 0 : 1);
  return Array.from(groups.values())
    .map((g) => ({ ...g, lines: g.lines.sort((a, b) => a.entity.localeCompare(b.entity) || (a.entityId ?? '').localeCompare(b.entityId ?? '') || (a.field ?? '').localeCompare(b.field ?? '')) }))
    .sort((a, b) => rank(a.severity) - rank(b.severity) || a.code.localeCompare(b.code));
}

/** The editor of an issue's entity on the branch, when one exists. */
export function issueHref(line: Pick<IssueLine, 'entity' | 'entityId'>, ctx: { org: string; game: string; branch: string; resolve?: (id: string) => { template: string } | undefined }): string | null {
  const id = line.entityId;
  switch (line.entity) {
    case 'document': {
      const template = id ? ctx.resolve?.(id)?.template : undefined;
      return id ? (template ? `${routes.documents(ctx.org, ctx.game, ctx.branch, template)}?doc=${encodeURIComponent(id)}` : routes.search(ctx.org, ctx.game, ctx.branch, id)) : null;
    }
    case 'template':
      return id ? routes.template(ctx.org, ctx.game, ctx.branch, id) : routes.templates(ctx.org, ctx.game, ctx.branch);
    case 'enum':
      return id ? routes.enumEditor(ctx.org, ctx.game, ctx.branch, id) : routes.enums(ctx.org, ctx.game, ctx.branch);
    case 'string':
    case 'localization_string':
      return routes.branch(ctx.org, ctx.game, ctx.branch, 'localization/strings') + (id ? `?search=${encodeURIComponent(id)}` : '');
    case 'asset':
      return routes.branch(ctx.org, ctx.game, ctx.branch, 'assets/files') + (id ? `?asset=${encodeURIComponent(id)}` : '');
    case 'product':
      return routes.branch(ctx.org, ctx.game, ctx.branch, 'products');
    default:
      return null;
  }
}

export function ValidationReport({ issues, branch, commit, ok, errors, warnings }: { issues: ValidationIssue[]; branch: string; commit?: string | null; ok?: boolean; errors?: number | string; warnings?: number | string }) {
  const { org, game } = useGame();
  const catalog = useOptionalContent();
  const groups = useMemo(() => groupIssues(issues), [issues]);
  const ctx = { org, game: game.slug, branch, resolve: catalog?.resolve };
  const errorCount = errors ?? issues.filter((i) => i.severity === 'error').length;
  const warningCount = warnings ?? issues.filter((i) => i.severity === 'warning').length;
  const passed = ok ?? Number(errorCount) === 0;
  return (
    <div className="validation-report" data-testid="validation-report">
      <div className="row validation-summary">
        <Badge tone={passed ? 'success' : 'danger'}>{passed ? 'OK' : 'Failed'}</Badge>
        <span>
          {errorCount} error{Number(errorCount) === 1 ? '' : 's'} · {warningCount} warning{Number(warningCount) === 1 ? '' : 's'}
        </span>
        {commit && (
          <code className="small muted" title={commit}>
            at {commit.slice(0, 10)}
          </code>
        )}
      </div>
      {groups.length === 0 && <p className="muted validation-clean">No issue.</p>}
      {groups.map((g) => (
        <section key={`${g.severity}:${g.code}`} className="issue-group" data-testid={`issue-group-${g.code}`} aria-label={g.code}>
          <h3 className="row issue-group-title">
            <Badge tone={g.severity === 'error' ? 'danger' : 'warning'}>{g.severity}</Badge>
            <code>{g.code}</code>
            <span className="muted small">{g.count}</span>
          </h3>
          <ul className="issue-lines">
            {g.lines.map((l, i) => {
              const href = issueHref(l, ctx);
              const who = `${l.entity}${l.entityId ? ` ${l.entityId.length > 12 ? l.entityId.slice(0, 8) + '…' : l.entityId}` : ''}${l.field ? ` · ${l.field}` : ''}`;
              return (
                <li key={i} className="issue-line">
                  {href ? <Link to={href}>{who}</Link> : <span>{who}</span>}
                  <span className="issue-message">{l.message}</span>
                  {l.count > 1 && <Badge title="The same issue, this many times">×{l.count}</Badge>}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
